import { GoogleTagManagerService } from 'gtm';
import { ParsedQuery } from 'query-string';
import { LanguageService } from 'services/language-service';
import { AuthTokens } from 'shared/interfaces/AuthTokens';
import {
  AuthChangeTemporaryPasswordDto,
  AuthResetPasswordDto,
  AuthResetPasswordRequestDto,
  RegisterSuperAdminDto,
  SignUpVerificationDto,
  UserLoginDto
} from 'shared/interfaces/User';
import { axiosUnauthenticatedInstance } from 'shared/utils/axios-instances';

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const authBaseEndpoint = `/auth`;

export const AuthService = {
  login: async (userLoginDto: UserLoginDto): Promise<void> => {
    const response = await axiosUnauthenticatedInstance({
      method: 'post',
      url: `${authBaseEndpoint}/login`,
      data: {
        ...userLoginDto,
        email: userLoginDto.email.toLowerCase()
      }
    });

    const authTokens = response.data;
    setSession(authTokens.accessToken, authTokens.refreshToken);
  },

  registerSuperAdmin: async (registerSuperAdminDto: RegisterSuperAdminDto): Promise<void> => {
    // Fire signup event in GTM
    GoogleTagManagerService.sendSignupEvent();

    await axiosUnauthenticatedInstance({
      method: 'post',
      url: `${authBaseEndpoint}/register-super-admin`,
      data: {
        ...registerSuperAdminDto,
        email: registerSuperAdminDto.email.toLowerCase(),
        locale: LanguageService.getCurrentLanguage(),
        verificationLinkBaseUrl: window.location.origin
      }
    });
  },

  authWithGoogle: async (code: string): Promise<void> => {
    const response = await axiosUnauthenticatedInstance({
      method: 'post',
      url: `${authBaseEndpoint}/google-auth`,
      data: {
        code
      }
    });
    const authTokens = response.data;
    setSession(authTokens.accessToken, authTokens.refreshToken);
  },

  getGoogleAuthUrl: async (queryParams?: ParsedQuery): Promise<string> => {
    const response = await axiosUnauthenticatedInstance({
      method: 'get',
      url: `${authBaseEndpoint}/google-auth-url`,
      params: queryParams
    });
    return response.data.url;
  },

  verifySignUp: async (signUpVerificationDto: SignUpVerificationDto): Promise<void> => {
    await axiosUnauthenticatedInstance({
      method: 'post',
      url: `${authBaseEndpoint}/verify-sign-up`,
      data: {
        ...signUpVerificationDto,
        email: signUpVerificationDto.email.toLowerCase()
      }
    });
  },

  getAuthToken: (): string | null => localStorage.getItem(ACCESS_TOKEN_KEY),

  getRefreshToken: (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY),

  isAuthenticated: (): boolean => AuthService.getAuthToken() !== null && AuthService.getRefreshToken() !== null,

  refreshAuthToken: async (): Promise<void> => {
    const response = await axiosUnauthenticatedInstance({
      method: 'post',
      url: `${authBaseEndpoint}/refresh`,
      data: { refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY) }
    });

    const authTokens = response.data as AuthTokens;
    setSession(authTokens.accessToken, authTokens.refreshToken);
  },

  clearAuthSession: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  changeTemporaryPassword: async (resetPasswordDto: AuthChangeTemporaryPasswordDto): Promise<void> => {
    const response = await axiosUnauthenticatedInstance({
      method: 'post',
      url: `${authBaseEndpoint}/change-temporary-password`,
      data: {
        ...resetPasswordDto,
        email: resetPasswordDto.email.toLowerCase()
      }
    });

    const authTokens = response.data;
    setSession(authTokens.accessToken, authTokens.refreshToken);
  },

  requestResetPasswordCode: async (resetPasswordDto: AuthResetPasswordRequestDto): Promise<void> => {
    await axiosUnauthenticatedInstance({
      method: 'post',
      url: `${authBaseEndpoint}/forget-password-verification-code`,
      data: {
        ...resetPasswordDto,
        email: resetPasswordDto.email.toLowerCase(),
        locale: LanguageService.getCurrentLanguage()
      }
    });
  },

  resetFogottenPassword: async (resetForgottenPasswordDto: AuthResetPasswordDto): Promise<void> => {
    await axiosUnauthenticatedInstance({
      method: 'post',
      url: `${authBaseEndpoint}/confirm-reset-password`,
      data: {
        ...resetForgottenPasswordDto,
        email: resetForgottenPasswordDto.email.toLowerCase()
      }
    });
  }
};

const setSession = (accessToken: string, refreshToken: string) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};
